import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchJson, resolveApiUrl } from '../utils/fetchJson';
import { ensureBrandingFontsLoaded } from '../utils/brandingFonts';

const PlatformContext = createContext({ platformName: 'Encatar', platformLogoUrl: null, logoSize: 36, platformNameFont: null, platformNameColor: null, platformNameFontSize: null, platformNameFontWeight: null, brandingDisplay: 'both', brandingGap: 8, refreshBranding: () => {} });

const configuredBaseRaw = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.VITE_API_BASE_URL ||
  process.env.NEXT_PUBLIC_API_BASE_URL ||
  ''
).trim();

function resolveAbsoluteAssetUrl(pathOrUrl) {
  const raw = (pathOrUrl || '').toString().trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  const base = configuredBaseRaw || (typeof window !== 'undefined' ? window.location.origin : '');
  const baseClean = String(base || '').replace(/\/$/, '');
  if (!baseClean) return raw;
  const rel = raw.startsWith('/') ? raw : `/${raw}`;
  return `${baseClean}${rel}`;
}

function setFaviconHref(href) {
  if (typeof document === 'undefined') return;
  const resolved = (href || '').toString().trim();
  const nextHref = resolved || resolveApiUrl('/api/global-settings/branding/logo');

  const selectors = [
    'link[rel="icon"]',
    'link[rel="shortcut icon"]'
  ];
  let found = false;
  for (const sel of selectors) {
    const el = document.querySelector(sel);
    if (el) {
      el.setAttribute('href', nextHref);
      found = true;
    }
  }
  if (!found) {
    const link = document.createElement('link');
    link.setAttribute('rel', 'icon');
    link.setAttribute('href', nextHref);
    document.head.appendChild(link);
  }
}

function setHeadLinkHref(rel, href) {
  if (typeof document === 'undefined') return;
  const resolved = (href || '').toString().trim();
  if (!resolved) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', resolved);
}

function setHeadMetaContent(attributeName, attributeValue, content) {
  if (typeof document === 'undefined') return;
  const resolvedContent = (content || '').toString().trim();
  if (!resolvedContent) return;
  let el = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attributeName, attributeValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', resolvedContent);
}

export function PlatformProvider({ children }) {
  const [platformName, setPlatformName] = useState('Encatar');
  const [platformLogoUrl, setPlatformLogoUrl] = useState(null);
  const [logoSize, setLogoSize] = useState(36);
  const [platformNameFont, setPlatformNameFont] = useState(null);
  const [platformNameColor, setPlatformNameColor] = useState(null);
  const [platformNameFontSize, setPlatformNameFontSize] = useState(null);
  const [platformNameFontWeight, setPlatformNameFontWeight] = useState(null);
  const [brandingDisplay, setBrandingDisplay] = useState('both');
  const [brandingGap, setBrandingGap] = useState(8);

  const fetchBranding = useCallback(async () => {
    try {
      const data = await fetchJson('/api/global-settings/branding', { skipAuth: true });
      if (data?.platformName) setPlatformName(data.platformName);
      if (data?.platformLogoUrl !== undefined) setPlatformLogoUrl(data.platformLogoUrl || null);
      if (Number.isFinite(data?.logoSize)) setLogoSize(data.logoSize);
      if (data?.platformNameFont !== undefined) setPlatformNameFont(data.platformNameFont || null);
      if (data?.platformNameColor !== undefined) setPlatformNameColor(data.platformNameColor || null);
      if (Number.isFinite(data?.platformNameFontSize)) setPlatformNameFontSize(data.platformNameFontSize);
      if (data?.platformNameFontWeight !== undefined) setPlatformNameFontWeight(data.platformNameFontWeight || null);
      if (data?.brandingDisplay) setBrandingDisplay(data.brandingDisplay);
      if (Number.isFinite(data?.brandingGap)) setBrandingGap(data.brandingGap);
    } catch (_) {}
  }, []);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  // Listen for branding updates (triggered after save in GlobalSettingsPage)
  useEffect(() => {
    const handler = () => fetchBranding();
    window.addEventListener('platform:branding:updated', handler);
    return () => window.removeEventListener('platform:branding:updated', handler);
  }, [fetchBranding]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    try {
      if (platformName) document.title = platformName;
    } catch (_) {}
    try {
      const brandingLogoHref = platformLogoUrl
        ? resolveAbsoluteAssetUrl(platformLogoUrl)
        : resolveApiUrl('/api/global-settings/branding/logo');
      setFaviconHref(brandingLogoHref);
      setHeadLinkHref('apple-touch-icon', brandingLogoHref);
      setHeadMetaContent('property', 'og:image', brandingLogoHref);
      setHeadMetaContent('name', 'twitter:image', brandingLogoHref);
    } catch (_) {}
  }, [platformName, platformLogoUrl]);

  useEffect(() => {
    ensureBrandingFontsLoaded([platformNameFont]);
  }, [platformNameFont]);

  return (
    <PlatformContext.Provider value={{ platformName, platformLogoUrl, logoSize, platformNameFont, platformNameColor, platformNameFontSize, platformNameFontWeight, brandingDisplay, brandingGap, refreshBranding: fetchBranding }}>
      {children}
    </PlatformContext.Provider>
  );
}

export function usePlatform() {
  return useContext(PlatformContext);
}

export default PlatformContext;
